import React, { useContext, useState, useEffect, createContext } from "react";
import { PageLoader } from "../common/Spinner";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import { auth, app } from "../lib/firebase";

const db = getFirestore(app);
const AuthContext = createContext();

// The profile (role) of the signed-in account, kept on this device so pages
// behind a role check still open without signal — a timing officer at a water
// station reloading the input page. Only used when the live read fails while
// offline; the servers check the role again on every request.
const PROFILE_KEY = (uid) => `vx_profile_${uid}`;
const cacheProfile = (uid, data) => {
  try { localStorage.setItem(PROFILE_KEY(uid), JSON.stringify({ role: data?.role ?? null })); } catch { /* storage full or blocked */ }
};
const cachedProfile = (uid) => {
  try { return JSON.parse(localStorage.getItem(PROFILE_KEY(uid)) || "null"); } catch { return null; }
};
const isOffline = () => typeof navigator !== "undefined" && navigator.onLine === false;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setFirebaseUser(firebaseUser);
      if (firebaseUser) {
        // Offline: Firestore would wait ~10 s before failing; use the cached profile.
        const cached = isOffline() ? cachedProfile(firebaseUser.uid) : null;
        if (cached) {
          setUser({ ...firebaseUser, ...cached });
        } else {
          try {
            // Fetch additional user data (like role) from Firestore
            const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              cacheProfile(firebaseUser.uid, userData);
              setUser({ ...firebaseUser, ...userData });
            } else {
              // Fallback if no firestore doc exists
              setUser(firebaseUser);
            }
          } catch (error) {
            console.error("Error fetching user data:", error);
            setUser({ ...firebaseUser, ...(cachedProfile(firebaseUser.uid) || {}) });
          }
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const getIdToken = async () => {
    if (!firebaseUser) {
      throw new Error("No user is signed in.");
    }
    return await firebaseUser.getIdToken();
  };

  const logout = () => {
    // A shared phone handed to the next officer must not keep this role.
    if (firebaseUser) { try { localStorage.removeItem(PROFILE_KEY(firebaseUser.uid)); } catch { /* blocked */ } }
    return signOut(auth);
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <AuthContext.Provider value={{ user, logout, loading, getIdToken }}>
      {children}
    </AuthContext.Provider>
  );
};
